import { Icon } from "./Icon.js";
import { fmt } from "../lib/money.js";
import { patchGoal } from "../api/goals.js";
import type { KidBundle } from "../api/me.js";

export interface AllocRowProps {
  goal: KidBundle["goals"][number];
  kid: KidBundle["user"];
  /** Monthly income in cents. */
  income: number;
  kidId: string;
  onUpdated: () => void | Promise<void>;
}

export function AllocRow({ goal, kid, income, kidId, onUpdated }: AllocRowProps) {
  const amt = goal.allocType === "$" ? goal.allocValue : (income * goal.allocValue) / 100;
  const color = kid.color ?? "var(--brand)";
  const colorSoft = kid.colorSoft ?? "var(--brand-soft)";

  // Display value: dollars (for $) or percent (for %)
  const displayValue = goal.allocType === "$" ? goal.allocValue / 100 : goal.allocValue;
  // Slider max: in dollars or percent
  const sliderMax = goal.allocType === "$" ? Math.max(1, Math.round(income / 100)) : 100;

  const setType = async (type: "$" | "%") => {
    if (type === goal.allocType) return;
    const value =
      type === "$"
        ? Math.round(amt) // amt is already in cents
        : income > 0
          ? Math.round((amt / income) * 100)
          : 0;
    await patchGoal(kidId, goal.rowKey, { allocType: type, allocValue: value });
    await onUpdated();
  };

  const setDisplay = async (n: number) => {
    if (Number.isNaN(n)) return;
    const value = goal.allocType === "$" ? Math.round(n * 100) : Math.round(n);
    await patchGoal(kidId, goal.rowKey, { allocType: goal.allocType, allocValue: value });
    await onUpdated();
  };

  return (
    <div>
      <div className="row-between" style={{ marginBottom: 6 }}>
        <div className="row">
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: colorSoft,
              color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon name={goal.icon} size={14} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 500 }}>{goal.title}</div>
        </div>
        <div className="row" style={{ gap: 6 }}>
          <div className="seg" style={{ padding: 2 }}>
            <button
              style={{ padding: "4px 10px", fontSize: 12 }}
              className={goal.allocType === "$" ? "active" : ""}
              onClick={() => void setType("$")}
            >
              $
            </button>
            <button
              style={{ padding: "4px 10px", fontSize: 12 }}
              className={goal.allocType === "%" ? "active" : ""}
              onClick={() => void setType("%")}
            >
              %
            </button>
          </div>
          <input
            type="number"
            value={displayValue}
            onChange={(e) => void setDisplay(Number(e.target.value))}
            style={{
              width: 64,
              padding: "6px 8px",
              border: "1px solid var(--hairline)",
              borderRadius: 8,
              textAlign: "right",
              fontSize: 13,
            }}
          />
          <div className="money money-sm" style={{ minWidth: 70, textAlign: "right" }}>
            {fmt(amt)}
          </div>
        </div>
      </div>
      <input
        type="range"
        min="0"
        max={sliderMax}
        step={1}
        value={displayValue}
        onChange={(e) => void setDisplay(Number(e.target.value))}
        style={{ width: "100%", accentColor: color }}
      />
    </div>
  );
}
